
export interface ClauseFinding { category: string; text: string; context: string; risk: string; explanation: string; suggestion: string; standardAlternative: string; }
export interface MissingProtection { protection: string; importance: string; suggestion: string; }
export interface AnalysisResult { flaggedClauses: ClauseFinding[]; missingProtections: MissingProtection[]; overallRisk: string; score: number; }

const DANGEROUS_CLAUSES = [
  {
    category: 'unlimited_liability',
    patterns: [/\bunlimited liability\b/gi, /\bwithout limit\b/gi, /\ball damages\b/gi, /\bany and all losses\b/gi, /\bfull responsibility\b/gi, /\bindemnify.*all claims\b/gi],
    risk: 'CRITICAL',
    explanation: "You could be held liable for unlimited damages, including client\'s lost profits, reputational harm, or third-party claims. This could bankrupt you.",
    suggestion: 'Cap liability at the total contract value or 12 months of fees, whichever is higher. Exclude indirect, consequential, and punitive damages.',
    standardAlternative: "Consultant\'s liability shall be limited to the total fees paid under this agreement in the 12 months preceding the claim. Neither party shall be liable for indirect, incidental, consequential, or punitive damages."
  },
  {
    category: 'non_compete',
    patterns: [/\bnon[-\s]?compete\b/gi, /\bnot compete\b/gi, /\bexclusive.*service\b/gi, /\bshall not provide.*similar\b/gi, /\bshall not work.*competitor\b/gi],
    risk: 'HIGH',
    explanation: "Non-competes can prevent you from working in your field for months or years. Many jurisdictions won't enforce them, but they can still create legal battles.",
    suggestion: "Replace with non-solicitation of specific clients (not general industry ban). Limit duration to 6-12 months. Limit geographic scope to client's direct market.",
    standardAlternative: "For 6 months following termination, Consultant shall not solicit Client's active clients for competing services. This does not restrict Consultant from working in the same industry or with other clients."
  },
  {
    category: 'ip_grab',
    patterns: [/\ball work product\b/gi, /\ball intellectual property\b/gi, /\bassigns all rights\b/gi, /\bwork for hire.*all\b/gi, /\btransfer.*all.*ip\b/gi, /\bownership.*all deliverables\b/gi],
    risk: 'HIGH',
    explanation: 'Client claims ownership of everything you create, including your tools, templates, and reusable code. You lose the right to reuse your own work.',
    suggestion: 'Distinguish between: (a) Custom deliverables created specifically for client — transfer upon full payment, (b) Pre-existing tools, templates, methodologies — consultant retains ownership, (c) General knowledge and skills — consultant retains.',
    standardAlternative: 'Client receives ownership of custom deliverables upon full payment. Consultant retains ownership of pre-existing tools, templates, and methodologies. Consultant may reuse general knowledge and skills gained.'
  },
  {
    category: 'unlimited_revisions',
    patterns: [/\bunlimited revisions\b/gi, /\buntil satisfied\b/gi, /\buntil approved\b/gi, /\bas many rounds\b/gi, /\bno limit.*changes\b/gi, /\bclient satisfaction\b/gi],
    risk: 'HIGH',
    explanation: 'No endpoint means client can request changes forever. Projects never close. You work indefinitely for fixed price.',
    suggestion: 'Define exactly X rounds of revisions per deliverable. Define what constitutes a "revision" (not a complete redesign). Charge hourly for changes beyond scope.',
    standardAlternative: 'Up to 3 rounds of revisions per deliverable. A revision is modification of existing work, not new direction. Additional revisions billed at $[RATE]/hour.'
  },
  {
    category: 'auto_renewal',
    patterns: [/\bautomatically renew\b/gi, /\bauto[-\s]?renew\b/gi, /\brenew.*unless.*terminate\b/gi, /\bcontinue.*until.*notice\b/gi, /\bperpetual.*until\b/gi],
    risk: 'MEDIUM',
    explanation: 'Contract renews automatically unless you remember to cancel. Easy to forget. Can lock you into unfavorable terms.',
    suggestion: 'Require explicit written renewal by both parties. Or: auto-renew for same term only if no objection received 30 days before expiry.',
    standardAlternative: 'This agreement does not auto-renew. Either party may propose renewal via written notice 30 days before expiry. New terms require mutual agreement.'
  },
  {
    category: 'vague_scope',
    patterns: [/\band other duties\b/gi, /\bas assigned\b/gi, /\bas needed\b/gi, /\band related tasks\b/gi, /\bincluding but not limited to\b/gi, /\bsuch other\b/gi, /\betc\.?\b/gi, /\band so on\b/gi],
    risk: 'HIGH',
    explanation: 'Vague scope language allows client to add unlimited work without additional payment. "And other duties" means anything they want.',
    suggestion: 'Use explicit scope document (SOW) attached as Exhibit A. State: "Work outside Exhibit A requires written change order and additional compensation."',
    standardAlternative: 'Scope of work is defined in Exhibit A. Any work outside Exhibit A requires a written Change Order with additional fees.'
  },
  {
    category: 'unfavorable_arbitration',
    patterns: [/\barbitration.*client.*city\b/gi, /\barbitration.*[client|company].*choice\b/gi, /\bbinding arbitration\b/gi, /\bwaive.*right.*court\b/gi, /\bwaive.*jury trial\b/gi],
    risk: 'MEDIUM',
    explanation: 'Arbitration can be expensive and favor the party with deeper pockets. Location chosen by client may require expensive travel.',
    suggestion: 'If arbitration required: mutual agreement on arbitrator, split costs, location neutral or your jurisdiction, option to seek injunctive relief in court.',
    standardAlternative: 'Disputes resolved through mediation, then binding arbitration if mediation fails. Arbitrator selected mutually. Location: [Neutral City]. Each party bears own costs. Either party may seek injunctive relief in court.'
  },
  {
    category: 'payment_risk',
    patterns: [/\bnet\s*60\b/gi, /\bnet\s*90\b/gi, /\bpayment.*completion.*project\b/gi, /\bpayment.*acceptance\b/gi, /\bpayment.*satisfactory\b/gi, /\bno payment.*until\b/gi, /\bmilestone.*approval.*payment\b/gi],
    risk: 'HIGH',
    explanation: 'Long payment terms or payment contingent on subjective approval = cash flow death. "Net 60" means you work 2+ months before getting paid.',
    suggestion: 'Require deposit (25-50%) before work begins. Milestone payments due upon delivery (not approval). Net 15 or Net 30 maximum. Kill fee if client cancels.',
    standardAlternative: 'Deposit: 50% due upon contract signing. Milestone payments due within 15 days of deliverable delivery. Late payments subject to 1.5% monthly service charge.'
  },
  {
    category: 'kill_fee_missing',
    patterns: [/\bterminate.*without cause.*no.*fee\b/gi, /\bterminate.*convenience.*no.*compensation\b/gi, /\bcancel.*no.*payment\b/gi, /\bno.*payment.*work.*completed\b/gi],
    risk: 'HIGH',
    explanation: "Client can cancel anytime and pay nothing, even after you've done significant work. No protection for your time investment.",
    suggestion: 'Include kill fee: 50% of total project value if cancelled after kickoff, 75% if cancelled after 50% completion, 100% if cancelled after final deliverable.',
    standardAlternative: 'If Client terminates after work begins, Client pays for all work completed to date plus a kill fee of 50% of remaining contract value.'
  }
];

const REQUIRED_PROTECTIONS = [
  { pattern: /\bscope of work\b|\bstatement of work\b|\bexhibit a\b|\bsow\b/gi, name: 'scope', importance: 'CRITICAL' },
  { pattern: /\bpayment terms\b|\binvoice\b|\bmilestone\b|\bdeposit\b/gi, name: 'payment', importance: 'CRITICAL' },
  { pattern: /\bintellectual property\b|\bwork product\b|\bownership\b|\blicense\b/gi, name: 'ip', importance: 'CRITICAL' },
  { pattern: /\bterminate\b|\btermination\b|\bkill fee\b|\bcancel\b/gi, name: 'termination', importance: 'IMPORTANT' },
  { pattern: /\bliability\b|\blimitation\b|\bcap\b|\bdamages\b/gi, name: 'liability', importance: 'IMPORTANT' },
  { pattern: /\bconfidential\b|\bnda\b|\bnon[-\s]?disclosure\b/gi, name: 'confidentiality', importance: 'IMPORTANT' },
  { pattern: /\bdispute\b|\barbitration\b|\bmediation\b|\bgoverning law\b/gi, name: 'dispute', importance: 'IMPORTANT' },
  { pattern: /\brevision\b|\bround\b|\bchange\b|\bscope creep\b/gi, name: 'revisions', importance: 'IMPORTANT' }
];

export function analyzeContract(contractText: string): AnalysisResult {
  const findings: ClauseFinding[] = [];
  let score = 100;

  DANGEROUS_CLAUSES.forEach(clause => {
    clause.patterns.forEach(pattern => {
      const matches = contractText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const index = contractText.indexOf(match);
          const start = Math.max(0, index - 80);
          const end = Math.min(contractText.length, index + 80);
          const context = contractText.substring(start, end).replace(/\n/g, ' ');

          findings.push({
            category: clause.category,
            text: match,
            context,
            risk: clause.risk,
            explanation: clause.explanation,
            suggestion: clause.suggestion,
            standardAlternative: clause.standardAlternative
          });
          score -= clause.risk === 'CRITICAL' ? 20 : clause.risk === 'HIGH' ? 15 : 10;
        });
      }
    });
  });

  const missing: MissingProtection[] = [];
  REQUIRED_PROTECTIONS.forEach(protection => {
    if (!protection.pattern.test(contractText)) {
      missing.push({
        protection: protection.name,
        importance: protection.importance,
        suggestion: `Add a ${protection.name} clause to protect both parties.`
      });
      score -= protection.importance === 'CRITICAL' ? 15 : 10;
    }
  });

  return {
    flaggedClauses: findings,
    missingProtections: missing,
    overallRisk: score >= 80 ? 'LOW' : score >= 60 ? 'MEDIUM' : score >= 40 ? 'HIGH' : 'CRITICAL',
    score: Math.max(0, score)
  };
}

export function quickScan(contractText: string): ClauseFinding[] {
  const allFlags: ClauseFinding[] = [];
  DANGEROUS_CLAUSES.forEach(clause => {
    clause.patterns.forEach(pattern => {
      const matches = contractText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          allFlags.push({
            category: clause.category,
            text: match,
            context: '',
            risk: clause.risk,
            explanation: clause.explanation,
            suggestion: clause.suggestion,
            standardAlternative: clause.standardAlternative
          });
        });
      }
    });
  });
  return allFlags
    .sort((a, b) => (b.risk === 'CRITICAL' ? 3 : b.risk === 'HIGH' ? 2 : 1) - (a.risk === 'CRITICAL' ? 3 : a.risk === 'HIGH' ? 2 : 1))
    .slice(0, 5);
}

export function generateNegotiationEmail(findings: ClauseFinding[]): string {
  const critical = findings.filter(f => f.risk === 'CRITICAL' || f.risk === 'HIGH');
  let email = `Subject: Contract Review — Request for Amendments\n\nHi [Client Name],\n\nThank you for sending over the contract. After reviewing it carefully, I have a few concerns that I'd like to address before we proceed.\n\n`;
  critical.forEach((f, i) => {
    email += `${i + 1}. **${f.category.replace(/_/g, ' ').toUpperCase()}**\n`;
    email += `   - Concern: ${f.explanation}\n`;
    email += `   - Suggested language: "${f.standardAlternative.slice(0, 120)}..."\n\n`;
  });
  email += `I'm excited to work together and want to make sure we have a fair, clear agreement that protects both of us. Would you be open to discussing these points?\n\nBest,\n[Your Name]`;
  return email;
}
